import type JCE from '@chesscom/js-chess-engine';

/**
 * Parse a positions effect data strings into a map with each square as a key and formatted data as a value
 *
 * @param {JCE.Marking.Collection} effectsMap - The markings map to parse.
 * @param {string} [square] - Optional specific square to get effects for.
 * @returns {JCE.Marking.Collection | undefined} An object where each key is a square (e.g., 'd4') and its value is an object containing properties for that square, or a single object if a square is specified.
 */
export function parseEffects(
  effectsMap: JCE.Marking.Collection,
  square?: string,
): JCE.Marking.Collection | undefined {
  const parsedData = {};

  for (const key in effectsMap) {
    if (Object.prototype.hasOwnProperty.call(effectsMap, key)) {
      const { data } = effectsMap[key];
      parsedData[key] = {};

      for (let i = 0; i < data.length; i += 2) {
        const property = data[i];
        const value = data[i + 1];
        parsedData[key][property] = convertStringToType(value);
      }
    }
  }

  const result = square ? parsedData[square] || {} : parsedData;

  return Object.keys(result).length === 0 ? undefined : result;
}

/**
 * Converts a string to its respective JavaScript type.
 * Specifically handles conversion of 'true' and 'false' to boolean values,
 * and 'undefined' to the undefined type. Other strings are returned as is.
 * @param {string} value - The string value to be converted.
 * @returns {boolean | undefined | string} - The converted JavaScript type.
 */
export function convertStringToType(value: string): boolean | undefined | string {
  if (value === 'true') {
    return true;
  } else if (value === 'false') {
    return false;
  } else if (value === 'undefined') {
    return undefined;
  }
  return value;
}
